import React, { Component } from "react";
import { connect } from "react-redux";
import { fetchDeliveryTime } from "../../store/actions/deliveryTime";
import { checkoutV2Api } from "../api";
import {
  buildCheckoutLines,
  createCartFingerprint,
  getEffectiveCheckoutLines,
} from "../domain/cartContext";
import {
  createFulfillmentFingerprint,
  getFulfillmentIssues,
  normalizeFulfillmentContext,
} from "../domain/fulfillmentContext";
import { getCheckoutV2Compatibility } from "../domain/featureFlags";
import { mergeCartValidation } from "../domain/mergeCartValidation";
import {
  clearCheckoutV2Quote,
  setCheckoutV2CartValidation,
  setCheckoutV2Quote,
} from "../state/actions";
import {
  recordStorefrontV2Telemetry,
  STOREFRONT_V2_TELEMETRY,
} from "../telemetry";
import "./CheckoutQuoteSync.css";

const safeErrorCode = (value) =>
  `${value || ""}`
    .trim()
    .replace(/[^a-z0-9_-]/gi, "")
    .slice(0, 120);

const firstServerError = (source) => {
  const value = Array.isArray(source) ? source[0] : source;
  if (value && typeof value === "object")
    return safeErrorCode(value.code || value.reason || value.message);
  return safeErrorCode(value);
};

export const getQuoteSyncErrorCode = (error) => {
  const response = error?.response?.data || {};
  const data = response.data || response;
  return (
    safeErrorCode(error?.checkoutCode) ||
    firstServerError(error?.checkoutErrors) ||
    firstServerError(data.errors) ||
    safeErrorCode(data.code || data.reason) ||
    safeErrorCode(error?.message) ||
    "quote_refresh_failed"
  );
};

export const getQuoteSyncErrorMessage = (code, arabic = false) => {
  const messages = {
    booking_slot_unavailable: [
      "A booking time is no longer available. Edit the booking in your cart and choose another time.",
      "موعد الحجز لم يعد متاحاً. عدّل الحجز في السلة واختر موعداً آخر.",
    ],
    cart_context_rejected: [
      "Review the delivery details and try again.",
      "راجع تفاصيل التوصيل وحاول مرة أخرى.",
    ],
    fulfillment_time: [
      "Choose an available delivery or pickup time, then retry.",
      "اختر وقت تنفيذ متاحاً ثم حاول مرة أخرى.",
    ],
    delivery_time_unavailable: [
      "That time is no longer available. Choose another time.",
      "هذا الوقت لم يعد متاحاً. اختر وقتاً آخر.",
    ],
    delivery_time_required: [
      "Choose a delivery or pickup time, then retry.",
      "اختر موعد التوصيل أو الاستلام ثم حاول مرة أخرى.",
    ],
    delivery_time_expired: [
      "That time just expired. Retry to refresh the available times.",
      "انتهت صلاحية الموعد. حاول مجدداً لتحديث المواعيد المتاحة.",
    ],
    delivery_slot_unavailable: [
      "That time is no longer available. Retry for the latest times.",
      "لم يعد هذا الموعد متاحاً. حاول مجدداً لعرض أحدث المواعيد.",
    ],
    asap_authority_unavailable: [
      "Your ASAP estimate expired. Retry to get a fresh delivery time.",
      "انتهت صلاحية تقدير أقرب وقت. حاول مجدداً لتحديث الموعد.",
    ],
    asap_context_changed: [
      "Your delivery or pickup details changed. Retry to refresh the time.",
      "تغيّرت تفاصيل التوصيل أو الاستلام. حاول مجدداً لتحديث الموعد.",
    ],
    asap_window_mismatch: [
      "The ASAP estimate changed. Retry to use the latest time.",
      "تغيّر تقدير أقرب وقت. حاول مجدداً لاستخدام الموعد الأحدث.",
    ],
    asap_cart_changed: [
      "Your cart changed. Retry to refresh the delivery time and total.",
      "تغيّرت سلتك. حاول مجدداً لتحديث الموعد والمجموع.",
    ],
    scheduled_availability_unavailable: [
      "An item is not available at the selected time.",
      "أحد المنتجات غير متاح في الوقت المحدد.",
    ],
    branch_delivery_charge: [
      "Choose a delivery area and branch again.",
      "اختر منطقة التوصيل والفرع مرة أخرى.",
    ],
    country_context_mismatch: [
      "The delivery country changed. Review the location and retry.",
      "تغيّرت دولة التوصيل. راجع الموقع وحاول مرة أخرى.",
    ],
    map_pin_delivery_area_mismatch: [
      "The pinned location is outside the selected delivery area.",
      "الموقع المحدد خارج منطقة التوصيل المختارة.",
    ],
    storefront_cycle03_disabled: [
      "Checkout was updated for this store. Refresh the page and try again.",
      "تم تحديث إعدادات الدفع. حدّث الصفحة وحاول مرة أخرى.",
    ],
    NetworkError: [
      "We couldn’t reach the store. Check your connection and retry.",
      "تعذر الاتصال بالمتجر. تحقق من الاتصال وحاول مرة أخرى.",
    ],
    pricing_pending: [
      "The delivery price is still being confirmed. Retry when you’re ready.",
      "لا يزال سعر التوصيل قيد التأكيد. حاول مرة أخرى عندما تكون جاهزاً.",
    ],
  };
  const fallback = [
    "We couldn’t refresh the total. Your cart is safe—retry now or review delivery or pickup details.",
    "تعذر تحديث المجموع. سلتك محفوظة—حاول الآن أو راجع تفاصيل التوصيل.",
  ];
  return (messages[code] || fallback)[arabic ? 1 : 0];
};

const FULFILLMENT_REFRESH_ERROR_CODES = new Set([
  "fulfillment_time",
  "delivery_time_required",
  "delivery_time_unavailable",
  "delivery_time_expired",
  "delivery_time_invalid",
  "delivery_slot_unavailable",
  "scheduled_time_unavailable",
  "asap_authority_unavailable",
  "asap_context_changed",
  "asap_window_mismatch",
  "asap_cart_changed",
]);

export const shouldRefreshFulfillmentBeforeQuoteRetry = (errorCode) =>
  FULFILLMENT_REFRESH_ERROR_CODES.has(`${errorCode || ""}`);

const TRANSIENT_QUOTE_ERROR_CODES = new Set([
  "networkerror",
  "failedtofetch",
  "loadfailed",
  "err_network",
  "econnaborted",
  "etimedout",
  "timeout",
]);

export const shouldRetryQuoteInBackground = (error, errorCode) => {
  const normalizedCode = `${errorCode || ""}`.toLowerCase();
  if (
    TRANSIENT_QUOTE_ERROR_CODES.has(normalizedCode) ||
    normalizedCode.includes("networkerror") ||
    normalizedCode.includes("failedtofetch") ||
    normalizedCode.includes("timeout")
  )
    return true;

  const status = Number(error?.response?.status);
  return (
    status === 408 ||
    status === 425 ||
    status === 429 ||
    (status >= 500 && status <= 599)
  );
};

export const createCompatibilityPendingQuote = ({
  compatibility = {},
  contextFingerprint = null,
  currency = null,
} = {}) => ({
  success: true,
  valid: false,
  requiresRequote: true,
  quoteId: null,
  pricingStatus: "pending",
  pendingReasons: (compatibility.issues || []).slice(0, 20),
  contextFingerprint,
  currency,
  subtotal: null,
  discountTotal: null,
  deliveryRate: null,
  vatAmount: null,
  additionalFee: null,
  giftWrapping: null,
  orderTotal: null,
  walletApplied: null,
  pointsApplied: null,
  total: null,
  minimumOrderRemaining: null,
  meetsMinimum: false,
  paymentMethods: [],
  paymentMethodOptions: [],
  errors: [],
});

export class CheckoutQuoteSync extends Component {
  static defaultProps = {
    api: checkoutV2Api,
    backgroundRetryBaseMs: 1500,
    backgroundRetryMaxMs: 30000,
    backgroundRetryMaxAttempts: 6,
    debounceMs: 400,
    retryFeedbackMs: 550,
    telemetry: recordStorefrontV2Telemetry,
  };

  state = { status: "idle", errorCode: "" };
  timer = null;
  requestId = 0;
  backgroundRetryAttempts = 0;
  mounted = false;

  componentDidMount() {
    this.mounted = true;
    this.schedule();
  }

  componentDidUpdate(prevProps) {
    if (this.getRequestKey(prevProps) !== this.getRequestKey(this.props))
      this.schedule({
        paymentOnly:
          this.getRequestKey(prevProps, true) === this.getRequestKey(this.props, true),
      });
  }

  componentWillUnmount() {
    this.mounted = false;
    if (this.timer) clearTimeout(this.timer);
    this.requestId += 1;
  }

  getRequest = (props = this.props) => {
    // Navigation preferences are local UI state, not pricing inputs.
    const { extrasAcknowledged, ...draft } = props.draft || {};
    const context = {
      ...props.context,
      fingerprint: createFulfillmentFingerprint(props.context),
    };
    return {
      context,
      lines: buildCheckoutLines(props.cart),
      draft,
      contact: props.contact || {},
      promise: { isGift: props.promise?.isGift === true },
      promotions: {
        promo: props.cart.promos,
        discounts: props.cart.discounts,
        smartPromos: props.cart.smart_promos,
      },
      currencyCode: props.selectedCurrencyCode || null,
    };
  };

  getRequestKey = (props, ignorePaymentMethod = false) => {
    const request = this.getRequest(props);
    if (ignorePaymentMethod) delete request.draft.selectedPaymentMethod;
    const compatibility = this.getCompatibility(props);
    return JSON.stringify([
      request.context.fingerprint,
      createCartFingerprint(request.lines),
      request.draft,
      request.contact,
      request.promise,
      request.promotions,
      request.currencyCode,
      compatibility.issues,
    ]);
  };

  getCompatibility = (props = this.props) =>
    getCheckoutV2Compatibility(props.settings, {
      address: props.address,
      cart: props.cart,
      context: props.context,
      pathname: props.pathname,
      countries: props.countries,
      selectedCurrencyCode: props.selectedCurrencyCode,
      store: props.store,
    });

  schedule = ({ paymentOnly = false } = {}) => {
    if (this.timer) clearTimeout(this.timer);
    this.backgroundRetryAttempts = 0;
    // Invalidate validation/quote responses that were started for the prior
    // cart, context, tender, or extras snapshot before starting the debounce.
    this.requestId += 1;
    const request = this.getRequest();
    const keepSummary = Boolean(this.props.quote) && request.lines.length > 0;
    if (keepSummary) {
      // Preserve the summary and controls, never the old permission to place.
      // Routine refreshes must not replace the summary with a loading panel.
      // Fees, discounts and eligibility still come from the API before placing.
      this.props.setQuote({
        ...this.props.quote,
        quoteId: null,
        quote_id: null,
        expiresAt: null,
        refreshing: true,
        requiresRequote: true,
      });
    } else this.props.clearQuote();
    if (!request.lines.length) {
      this.setState({ status: "idle", errorCode: "" });
      return;
    }
    this.setState({ status: "background", errorCode: "" });
    // A click is already a discrete choice; unlike typing it needs no debounce.
    this.timer = setTimeout(
      () => this.fetchQuote(request, { reuseValidation: paymentOnly ? this.props.validation : null }),
      paymentOnly ? 0 : this.props.debounceMs,
    );
  };

  waitForRetryFeedback = async (startedAt) => {
    if (!startedAt) return;
    const minimum = Math.max(0, Number(this.props.retryFeedbackMs) || 0);
    const remaining = minimum - (Date.now() - startedAt);
    if (remaining > 0)
      await new Promise((resolve) => setTimeout(resolve, remaining));
  };

  scheduleBackgroundRetry = (requestId, errorCode = "quote_refresh_failed") => {
    this.backgroundRetryAttempts += 1;
    const maximumAttempts = Math.max(
      1,
      Number(this.props.backgroundRetryMaxAttempts) || 6,
    );
    if (this.backgroundRetryAttempts > maximumAttempts) {
      this.setState({ status: "error", errorCode });
      return;
    }
    const baseDelay = Math.max(
      250,
      Number(this.props.backgroundRetryBaseMs) || 1500,
    );
    const maximumDelay = Math.max(
      baseDelay,
      Number(this.props.backgroundRetryMaxMs) || 30000,
    );
    const delay = Math.min(
      maximumDelay,
      baseDelay * Math.pow(2, Math.min(this.backgroundRetryAttempts - 1, 5)),
    );
    const retry = () => {
      this.timer = null;
      if (!this.mounted || requestId !== this.requestId) return;
      if (typeof document !== "undefined" && document.hidden) {
        this.timer = setTimeout(retry, maximumDelay);
        return;
      }
      this.fetchQuote(this.getRequest());
    };
    this.timer = setTimeout(retry, delay);
  };

  refreshExpiredAsap = async ({
    errorCode,
    request,
    requestId,
    retryStartedAt,
    fulfillmentRefreshAttempted,
  }) => {
    if (
      fulfillmentRefreshAttempted ||
      request.context?.time?.type !== "asap" ||
      !shouldRefreshFulfillmentBeforeQuoteRetry(errorCode) ||
      !this.props.refreshDeliveryTime
    )
      return false;

    const refreshedDeliveryTime = await this.props.refreshDeliveryTime();
    // A connected delivery-time update can schedule a newer quote itself. In
    // that case this request has been superseded and must not render the stale
    // authority error while the fresh request is loading.
    if (!this.mounted || requestId !== this.requestId) return true;
    if (!refreshedDeliveryTime) return false;

    const refreshedContext = normalizeFulfillmentContext({
      address: this.props.address,
      branch: this.props.branch,
      deliveryTime: refreshedDeliveryTime,
      settings: this.props.settings,
    });
    const refreshedRequest = this.getRequest({
      ...this.props,
      context: refreshedContext,
    });
    await this.fetchQuote(refreshedRequest, {
      retryStartedAt,
      fulfillmentRefreshAttempted: true,
    });
    return true;
  };

  fetchQuote = async (
    request,
    {
      retryStartedAt = 0,
      fulfillmentRefreshAttempted = false,
      reuseValidation = null,
    } = {},
  ) => {
    const requestId = ++this.requestId;
    if (getFulfillmentIssues(request.context).length) {
      this.props.clearQuote();
      this.setState({ status: "idle", errorCode: "" });
      return;
    }
    try {
      if (
        request.context?.time?.type === "asap" &&
        !request.context.time.authority &&
        this.props.refreshDeliveryTime
      ) {
        const refreshedDeliveryTime = await this.props.refreshDeliveryTime();
        if (!this.mounted || requestId !== this.requestId) return;
        if (refreshedDeliveryTime) {
          const refreshedContext = normalizeFulfillmentContext({
            address: this.props.address,
            branch: this.props.branch,
            deliveryTime: refreshedDeliveryTime,
            settings: this.props.settings,
          });
          request = this.getRequest({
            ...this.props,
            context: refreshedContext,
          });
        }
      }
      const validation =
        reuseValidation?.valid === true &&
        reuseValidation.contextFingerprint === request.context.fingerprint &&
        reuseValidation.cartFingerprint === createCartFingerprint(request.lines)
          ? { ...reuseValidation, success: true }
          : await this.props.api.validateCartContext(request);
      if (!this.mounted || requestId !== this.requestId) return;
      const validationErrorCode = firstServerError(validation?.errors);
      if (
        (!validation?.success || !validation?.valid) &&
        (await this.refreshExpiredAsap({
          errorCode: validationErrorCode,
          request,
          requestId,
          retryStartedAt,
          fulfillmentRefreshAttempted,
        }))
      )
        return;
      this.props.setValidation({
        ...validation,
        contextFingerprint: request.context.fingerprint,
        cartFingerprint: createCartFingerprint(request.lines),
      });
      if (!validation?.success || !validation?.valid) {
        const rejection = new Error("cart_context_rejected");
        rejection.checkoutCode = validationErrorCode;
        rejection.checkoutErrors = validation?.errors;
        throw rejection;
      }
      const quote = await this.props.api.fetchCheckoutQuote({
        ...request,
        lines: getEffectiveCheckoutLines(request.lines, validation),
      });
      if (!this.mounted || requestId !== this.requestId) return;
      if (!quote?.success) {
        const quoteErrorCode = firstServerError(quote?.errors);
        if (
          await this.refreshExpiredAsap({
            errorCode: quoteErrorCode,
            request,
            requestId,
            retryStartedAt,
            fulfillmentRefreshAttempted,
          })
        )
          return;
        const rejection = new Error("quote_rejected");
        rejection.checkoutCode = quoteErrorCode;
        rejection.checkoutErrors = quote?.errors;
        throw rejection;
      }
      if (
        quote.contextFingerprint &&
        quote.contextFingerprint !== request.context.fingerprint
      )
        throw new Error("quote_context_stale");
      await this.waitForRetryFeedback(retryStartedAt);
      if (!this.mounted || requestId !== this.requestId) return;
      if (quote.cartValidation)
        this.props.setValidation({
          ...mergeCartValidation(validation, quote.cartValidation),
          contextFingerprint: request.context.fingerprint,
          cartFingerprint: createCartFingerprint(request.lines),
        });
      // A successful response without a quote token is the authoritative
      // bootstrap state used to render payment eligibility/errors. Replacing
      // the entire quote also guarantees that an older quoteId is cleared.
      this.props.setQuote({
        ...quote,
        refreshing: false,
        clientCartFingerprint: createCartFingerprint(getEffectiveCheckoutLines(request.lines, quote.cartValidation || validation)),
        contextFingerprint: request.context.fingerprint,
      });
      if (quote.pricingStatus === "pending") {
        this.setState({ status: "background", errorCode: "" });
        this.scheduleBackgroundRetry(requestId, "pricing_pending");
        return;
      }
      this.backgroundRetryAttempts = 0;
      this.setState({ status: "ready", errorCode: "" });
      if (quote.expiresAt) {
        const refreshIn = Math.min(
          2147483647,
          Math.max(1000, quote.expiresAt - Date.now()),
        );
        this.timer = setTimeout(
          () => this.schedule(),
          refreshIn,
        );
      }
    } catch (error) {
      if (!this.mounted || requestId !== this.requestId) return;
      await this.waitForRetryFeedback(retryStartedAt);
      if (!this.mounted || requestId !== this.requestId) return;
      const errorCode = getQuoteSyncErrorCode(error);
      if (error?.message === "quote_context_stale")
        (this.props.telemetry || recordStorefrontV2Telemetry)(
          STOREFRONT_V2_TELEMETRY.PRICE_QUOTE_MISMATCH,
          "checkout_quote",
        );
      const retryInBackground = shouldRetryQuoteInBackground(error, errorCode);
      if (!retryInBackground || !this.props.quote?.refreshing) this.props.clearQuote();
      if (retryInBackground) {
        // Connectivity and temporary server failures do not require shopper
        // input. Keep the checkout calm and retry the latest request with a
        // bounded exponential delay; a prop change cancels this generation.
        this.setState({ status: "background", errorCode: "" });
        this.scheduleBackgroundRetry(requestId, errorCode);
        return;
      }
      this.backgroundRetryAttempts = 0;
      this.setState({
        status: "error",
        errorCode,
      });
    }
  };

  retry = async () => {
    if (this.state.status === "loading") return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.backgroundRetryAttempts = 0;
    this.requestId += 1;
    const retryStartedAt = Date.now();
    const retryErrorCode = this.state.errorCode;
    let context = this.props.context;
    this.setState({ status: "loading", errorCode: "" });
    if (
      shouldRefreshFulfillmentBeforeQuoteRetry(retryErrorCode) &&
      this.props.refreshDeliveryTime
    ) {
      const refreshedDeliveryTime = await this.props.refreshDeliveryTime();
      if (!this.mounted) return;
      if (refreshedDeliveryTime) {
        context = normalizeFulfillmentContext({
          address: this.props.address,
          branch: this.props.branch,
          deliveryTime: refreshedDeliveryTime,
          settings: this.props.settings,
        });
      }
      // The delivery-time dispatch may also have scheduled a quote through
      // connected props. Cancel that debounce and make this explicit retry
      // the single owner of the immediate request.
      if (this.timer) clearTimeout(this.timer);
      this.timer = null;
      this.requestId += 1;
    }
    const request = this.getRequest({ ...this.props, context });
    return this.fetchQuote(request, { retryStartedAt });
  };

  render() {
    if (this.props.silent) return null;
    const arabic = this.props.language === "arabic";
    if (
      this.state.status === "idle" ||
      this.state.status === "ready" ||
      this.state.status === "background" ||
      this.state.status === "loading"
    )
      return null;
    return (
      <div
        className="checkout-v2-quote-status is-error"
        role="alert"
        data-checkout-v2-quote-status="error"
        data-checkout-v2-quote-error={this.state.errorCode}
      >
        <div>
          <strong>{arabic ? "تعذر تحديث الطلب" : "We couldn’t refresh your order"}</strong>
          <p>{getQuoteSyncErrorMessage(this.state.errorCode, arabic)}</p>
        </div>
        <button type="button" onClick={this.retry}>
          {arabic ? "إعادة المحاولة" : "Retry"}
        </button>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const store = state.store?.store || {};
  const settings = state.store?.store?.settings || {};
  const address = state.customers?.address;
  const branch = state.branch;
  const deliveryTime = state.deliveryTime;
  return {
    language: state.store?.language || "english",
    cart: state.cart || {},
    context: normalizeFulfillmentContext({
      address,
      branch,
      deliveryTime,
      settings,
    }),
    address,
    branch,
    deliveryTime,
    countries: store.all_countries || store.countries || [],
    selectedCurrencyCode: state.currency?.selected || null,
    store,
    pathname:
      typeof window === "undefined" ? "" : window.location.pathname,
    settings,
    draft: state.checkoutV2?.draft || {},
    quote: state.checkoutV2?.quote,
    validation: state.checkoutV2?.cartValidation,
    contact: state.customers?.customerContact || {},
    promise: { isGift: state.promise?.isGift === true },
  };
};

export default connect(mapStateToProps, (dispatch) => ({
  setQuote: (quote) => dispatch(setCheckoutV2Quote(quote)),
  setValidation: (validation) =>
    dispatch(setCheckoutV2CartValidation(validation)),
  clearQuote: () => dispatch(clearCheckoutV2Quote()),
  refreshDeliveryTime: () => dispatch(fetchDeliveryTime()),
}))(CheckoutQuoteSync);
